import { chartTypeEnum } from '../Analytics/constants';

type ChatChartType = (typeof chartTypeEnum)[keyof typeof chartTypeEnum];

export interface AssistantChartPayload {
  type: ChatChartType | string;
  chartData?: unknown;
  format?: 'series-v1';
  data?: {
    labels?: string[];
    series?: Array<{
      name: string;
      values: number[];
      color?: string;
    }>;
    rows?: Array<{
      label: string;
      value: number;
    }>;
    metrics?: {
      historical?: number;
      thisMonth?: number;
    };
  };
  title?: string;
  subTitle?: string;
  label?: string;
  query?: string;
  isYearlyChart?: boolean;
  analyticsVariant?: 'team' | 'custom' | 'default';
}

const sanitizeNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toRecordFromLabelsAndValues = (labels: string[], values: number[]): Record<string, number> => {
  const output: Record<string, number> = {};
  labels.forEach((label, index) => {
    output[label] = sanitizeNumber(values[index]);
  });
  return output;
};

const adaptSeriesV1ChartData = (chart: AssistantChartPayload): AssistantChartPayload => {
  if (chart.format !== 'series-v1') {
    return chart;
  }

  const labels = chart.data?.labels ?? [];
  const series = chart.data?.series ?? [];
  const primarySeries = series[0];
  const rows = chart.data?.rows ?? [];

  if (!labels.length && !rows.length && !series.length) {
    return chart;
  }

  switch (chart.type) {
    case chartTypeEnum.ChartJSBar:
      return {
        ...chart,
        chartData: [
          {
            x: rows.length ? rows.map((row) => sanitizeNumber(row.value)) : primarySeries?.values ?? [],
            y: rows.length ? rows.map((row) => row.label) : labels,
          },
        ],
      };
    case chartTypeEnum.ChartJSDoughnut:
      return {
        ...chart,
        chartData: [
          {
            labels: rows.length ? rows.map((row) => row.label) : labels,
            values: rows.length ? rows.map((row) => sanitizeNumber(row.value)) : primarySeries?.values ?? [],
            labelToColorMapping: Object.fromEntries(
              (series[0]?.color
                ? rows.length
                  ? rows
                  : labels.map((label, index) => ({ label, value: primarySeries?.values[index] }))
                : []
              ).map((item) => [item.label, series[0]?.color])
            ),
          },
        ],
      };
    case chartTypeEnum.ChartJSStackedBar:
      return {
        ...chart,
        chartData: series.map((item) => ({
          label: item.name,
          data: toRecordFromLabelsAndValues(labels, item.values),
        })),
      };
    case chartTypeEnum.wordCloud:
      return {
        ...chart,
        chartData: [
          {
            y: rows.length ? rows.map((row) => row.label) : labels,
            x: rows.length ? rows.map((row) => sanitizeNumber(row.value)) : primarySeries?.values ?? [],
          },
        ],
      };
    case chartTypeEnum.topHits:
      return {
        ...chart,
        chartData: [
          (rows.length ? rows : labels.map((label, index) => ({ label, value: primarySeries?.values[index] }))).map(
            (item) => ({
              _id: item.label,
              count: sanitizeNumber(item.value),
            })
          ),
        ],
      };
    case chartTypeEnum.numberBox: {
      const historical = sanitizeNumber(chart.data?.metrics?.historical ?? primarySeries?.values?.[0]);
      const thisMonth = sanitizeNumber(chart.data?.metrics?.thisMonth ?? primarySeries?.values?.[1]);
      return {
        ...chart,
        chartData: [{ y: [historical, thisMonth] }],
      };
    }
    case chartTypeEnum.bar:
      return {
        ...chart,
        chartData: series.map((item) => ({
          type: 'bar',
          name: item.name,
          x: labels,
          y: item.values.map((value) => sanitizeNumber(value)),
        })),
      };
    case chartTypeEnum.pie:
      return {
        ...chart,
        chartData: [
          {
            type: 'pie',
            labels: rows.length ? rows.map((row) => row.label) : labels,
            values: rows.length ? rows.map((row) => sanitizeNumber(row.value)) : primarySeries?.values ?? [],
          },
        ],
      };
    case chartTypeEnum.line:
    case chartTypeEnum.PlotlyMonthLine:
      return {
        ...chart,
        chartData: series.map((item) => ({
          type: 'scatter',
          mode: 'lines',
          name: item.name,
          x: labels,
          y: item.values.map((value) => sanitizeNumber(value)),
        })),
      };
    case chartTypeEnum.barVertical:
    case chartTypeEnum.barPercentage:
    default:
      return {
        ...chart,
        chartData: series.map((item) => ({
          year: item.name,
          data: toRecordFromLabelsAndValues(labels, item.values),
        })),
      };
  }
};

export const adaptAssistantChartPayload = (
  chart: AssistantChartPayload | null | undefined
): AssistantChartPayload | null => {
  if (!chart) {
    return null;
  }

  if (chart.format === 'series-v1') {
    return adaptSeriesV1ChartData(chart);
  }

  return chart;
};
